import { DATE_FORMAT, DISPLAY_DATE_FORMAT, DISPLAY_DATETIME_FORMAT, DISPLAY_TIME_FORMAT, TIME_FORMAT } from "../constants";
import moment from "../moment/moment";
import { padNumber } from "./utils";
import i18n from "@/i18n";

/**
 * Return the next time when the minutes are multiple of slice. If no dateTime is given, the current time will be used. 
 * @param {moment.Moment} dateTime 
 * @param {number} slice 
 * @returns {moment.Moment}
 */
export const getNextTime = (dateTime = moment(), slice = 5) => {
    const remainder = dateTime.minute() % slice === 0 ? 0 : slice - (dateTime.minute() % slice);
    dateTime.add(remainder, "minutes");
    return dateTime;
};

/**
 * Return the previous or the next time when the minutes are multiple of slice.
 * @param {moment.Moment} dateTime 
 * @param {number} slice 
 * @returns {moment.Moment}
 */
export const getRoundTime = (dateTime = moment(), slice = 5) => {
    const rest = dateTime.minute() % slice;
    dateTime.add(rest >= slice / 2 ? slice - rest : - rest, "minutes");
    return dateTime;
}

export const setToCurrentTime = (dateTime) => {
    dateTime = moment(dateTime);
    return dateTime.hour(moment().hour()).minute(moment().minute());
};

/**
 * 
 * @param {string} time 
 * @param {string} format - format of input time (default: `HH:mm:ss`)
 * @returns {moment.Moment}
 */
export const timeToMoment = (time, format = TIME_FORMAT) => moment(time, format);

/**
 * 
 * @param {moment.Moment} momentObj 
 * @param {string} format - format of ouput time (default: `HH:mm:ss`)
 * @returns {string}
 */
export const momentToTime = (momentObj, format = TIME_FORMAT) => momentObj.format(format);

export const getHoursBetween = (momentA = moment(), momentB = moment(), slice = 5) => {

    const result = [];

    momentA = momentA.clone();
    momentB = momentB.clone();

    while (momentA.isBefore(momentB)) {
        result.push(momentA.clone());
        momentA.add(slice, 'minutes');
    }

    result.push(momentB);

    return result;
}

/**
 * Check if a given date is in the past.
 * @param {moment.Moment} date 
 * @param {moment.Moment} startDate 
 * @returns 
 */
export const isPastDate = (date, startDate = moment()) => {
    const today = startDate.startOf('day');
    date = moment(date);
    return today.isAfter(date);
}

/**
 * Check if a year is a leap year or not.
 * @param {number} year 
 * @returns {boolean}
 */
export const isLeapYear = (year = 0) => (year > 1582 && ((year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0)));


/**
 * 
 * @param {moment.Moment} start 
 * @param {moment.Moment} end 
 */
export const getWeekdaysBetween = (start = moment(), end = moment()) => {

    if (start.isAfter(end)) {
        [start, end] = [end, start];
    }
    const daysDiff = end.diff(start, 'days');
    const weekdays = new Set();
    const currentDate = start.isoWeekday();

    for (let i = 0; i <= daysDiff; i++) {
        weekdays.add(((currentDate - 1 + i) % 7) + 1);
        if (weekdays.size == 7)
            break;
    }

    return weekdays;
}

/**
 * 
 * @param {string} durationString 
 */
export const buildDurationObject = durationString => {
    const duration = moment.duration(durationString);
    if (!duration.isValid()) {
        return {};
    }
    return {
        days: duration.days(),
        hours: duration.hours(),
        minutes: duration.minutes(),
        seconds: duration.seconds(),
        duration,
    };
}

export const buildDurationString = ({ days = 0, hours = 0, minutes = 0, seconds = 0 }) => {
    let [_hours, _minutes, _seconds] = [hours, minutes, seconds].map(padNumber);
    const _durationString = `${days} ${_hours}:${_minutes}:${_seconds}`;
    const duration = moment.duration(_durationString);
    [days, _hours, _minutes, _seconds] = [duration.days(), duration.hours(), duration.minutes(), duration.seconds()].map(padNumber);
    return `${days} ${_hours}:${_minutes}:${_seconds}`;
}

/**
 * Formats a moment duration object to a `[days] [hours]:[minutes]:[seconds]` string.
 * @param {moment.Duration} duration 
 */
export function formatDuration(duration) {
    const durationDays = Math.floor(duration.asDays());

    const paddedDays = durationDays > 0 ? padNumber(durationDays): '';
    const paddedHours = padNumber(duration.hours());
    const paddedMinutes = padNumber(duration.minutes());
    const paddedSeconds = padNumber(duration.seconds());

    const formattedDuration = `${paddedDays} ${paddedHours}:${paddedMinutes}:${paddedSeconds}`;
    const trimmedFormattedDuration = formattedDuration.trim();
    return trimmedFormattedDuration;
}

/**
 * 
 * @example
 * getFormattedDatetime("2026-04-23 14:30")
 * // "23/04/2026 14:30"
 *
 * @example
 * getFormattedDatetime("2026-04-23 14:30", { useCalendar: true })
 * // "Today at 14:30"
 *
 * @example
 * getFormattedDatetime("2026-04-24 09:00", { useCalendar: true })
 * // "Tomorrow at 09:00"
 */
export function getFormattedDatetime(datetime, options = {}) {
    const {
        format = DISPLAY_DATETIME_FORMAT,
        language = null,
        useCalendar = false,
    } = options;

    const m = moment(datetime);

    if (language) {
        const adjustedLang = language === "en" ? "en-gb" : language;
        m.locale(adjustedLang);
    }

    if (useCalendar) {
        return m.calendar(null, {
            sameDay: m.localeData().calendar("sameDay").replace("LT", "HH:mm"),
            nextDay: m.localeData().calendar("nextDay").replace("LT", "HH:mm"),
            lastDay: m.localeData().calendar("lastDay").replace("LT", "HH:mm"),
            sameElse: format,
        });
    }
    return m.format(format);
}

/**
 * Possible improvement: add a threshold to display a date after X days
 *   e.g. "10/04/2026" instead of "12 days ago"
 * 
 * @example
 * getRelativeTime("2026-04-23 14:00")
 * // "il y a 2 heures"
 *
 * @example
 * getRelativeTime("2026-04-23 18:00")
 * // "dans 2 heures"
 *
 * @example
 * getRelativeTime("2026-04-20 14:00")
 * // "il y a 3 jours"
 */
export function getRelativeTime(datetime) {
    return moment(datetime).fromNow();
}

export const getFormattedDate = (rawDate, format = DISPLAY_DATE_FORMAT) => moment(rawDate).format(format);



/**
 * Combines a date and a time as a Moment object.
 * @param {string} date 
 * @param {string} time 
 * @returns 
 */
export function combineDateAndTime(date, time) {
    return moment(
        `${date} ${time}`,
        `${DATE_FORMAT} ${TIME_FORMAT}`,
    )
}


/**
 * Sets the time of a given Moment.js datetime object from a string.
 *
 * @param {import('moment').Moment} [datetime=moment()] - The Moment.js object to modify.
 * @param {string} [time="00:00:00"] - Time string in the format "HH:mm:ss".
 * @returns {import('moment').Moment} - The modified Moment.js object with updated time.
 *
 * @example
 * const dt = moment("2025-12-16T10:30:00");
 * const newDt = setTimeFromString(dt, "14:45:30");
 * console.log(newDt.format()); // Output: "2025-12-16T14:45:30+00:00"
 */
export function setTimeFromString(
    datetime = moment(), 
    time = "00:00:00"
) {
    const [hours, minutes, seconds] = time
        .split(':')
        .map(el => parseInt(el, 10));
    
    return datetime
        .hours(hours)
        .minutes(minutes)
        .seconds(seconds);
}


/**
 * Comparison callback for sorting Moment.js objects in ascending order
 * based on their Unix timestamps.
 *
 * @param {import('moment').Moment} o1 - The first Moment.js object to compare.
 * @param {import('moment').Moment} o2 - The second Moment.js object to compare.
 * @returns {number} - Returns a negative number if o1 is before o2,
 *                     zero if they are equal,
 *                     or a positive number if o1 is after o2.
 *
 * @example
 * const dates = [moment("2025-12-16T12:00:00"), moment("2025-12-16T08:00:00")];
 * dates.sort(sortByUnixTimestampCallback);
 * console.log(dates.map(d => d.format())); 
 * // Output: ["2025-12-16T08:00:00", "2025-12-16T12:00:00"]
 */
export function sortByUnixTimestampCallback(o1, o2) {
    return o1.unix() - o2.unix();
}

export function formatHour(hour) {
    if (!hour) {
        return '';
    }
    return moment(hour, TIME_FORMAT)
        .format(DISPLAY_TIME_FORMAT);
}


// WEBPACK FOOTER //
// ./src/helpers/time.js